
import React, { useState } from 'react';
import { Product, Variant } from '../types';
import { Plus, Percent, Sparkles, Star, Zap, Ban } from 'lucide-react';
import { formatPrice } from '../constants';

interface ProductCardProps {
  product: Product;
  onAddToCart: (p: Product, v?: Variant) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const currentStock = Number(product.stock) || 0;
  const isOutOfStock = currentStock <= 0;
  const isPromo = product.originalPrice && product.originalPrice > product.price;

  const getBadgeConfig = (label: string) => {
    if (isOutOfStock) return { style: 'bg-red-600 text-white', icon: <Ban size={10} />, text: 'RUPTURE' };
    
    const l = label?.toLowerCase() || '';
    switch (l) {
      case 'best-seller':
        return { style: 'bg-brand-gold text-white', icon: <Star size={10} fill="currentColor" />, text: 'BEST-SELLER' };
      case 'promo':
        return { style: 'bg-rose-900 text-white', icon: <Percent size={10} strokeWidth={3} />, text: 'OFFRE PRIVÉE' };
      case 'nouveau':
        return { style: 'bg-emerald-800 text-white', icon: <Sparkles size={10} />, text: 'NOUVEAUTÉ' };
      case 'édition limitée':
        return { style: 'bg-brand-black text-brand-gold border border-brand-gold/40', icon: <Zap size={10} fill="currentColor" />, text: 'ÉDITION LIMITÉE' };
      default:
        return { style: 'bg-brand-black text-white', icon: null, text: label.toUpperCase() };
    }
  };

  const badge = getBadgeConfig(product.label || '');

  return (
    <div 
      className={`group luxury-transition ${isOutOfStock ? 'opacity-90' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-brand-cream mb-6">
        {/* Image principale - On retire le grayscale pour laisser imaginer la couleur */}
        <img 
          src={product.image} 
          alt={product.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-1000 ${isHovered ? 'opacity-0 scale-110' : 'opacity-100'}`}
        />
        
        {/* Image de détail (Hover) - Maintenant active même si stock = 0 */}
        {product.hoverImage && (
          <img 
            src={product.hoverImage} 
            alt={`${product.name} detail`}
            className={`absolute inset-0 w-full h-full object-cover luxury-transition ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          />
        )}

        <div className="absolute top-4 left-4 z-10 flex flex-col gap-1.5 items-start">
          {(product.label || isOutOfStock) && (
            <span className={`text-[8px] uppercase tracking-[0.2em] px-3 py-2 font-bold shadow-2xl flex items-center gap-2 luxury-transition ${badge.style}`}>
              {badge.icon}
              {badge.text}
            </span>
          )}
        </div>

        {/* Le bouton d'ajout reste masqué uniquement si vraiment en rupture */}
        {!isOutOfStock && (
          <button 
            onClick={(e) => {
              e.preventDefault();
              onAddToCart(product);
            }}
            className={`absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-brand-black w-12 h-12 rounded-full z-20 flex items-center justify-center shadow-2xl transition-all duration-700 ${
              isHovered ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
            } hover:bg-brand-gold hover:text-white`}
          >
            <Plus size={20} />
          </button>
        )}
      </div>

      <div className="text-center px-2">
        <h3 className={`font-serif text-lg mb-1 transition-colors ${isOutOfStock ? 'text-brand-black/70' : 'group-hover:text-brand-gold'}`}>
          {product.name}
        </h3>
        <p className={`text-[9px] uppercase tracking-widest mb-2 font-medium ${isOutOfStock ? 'text-gray-400' : 'text-gray-400 opacity-60'}`}>
          Collection {product.category}
        </p>
        <div className="flex flex-col items-center gap-1">
          {isPromo && !isOutOfStock && (
            <span className="text-[10px] text-gray-300 line-through decoration-1">
              {formatPrice(product.originalPrice!)}
            </span>
          )}
          <p className={`font-bold tracking-tight ${isOutOfStock ? 'text-red-700 text-[10px] uppercase tracking-widest' : isPromo ? 'text-rose-900' : 'text-brand-gold'}`}>
            {isOutOfStock ? 'Bientôt de retour' : formatPrice(product.price)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
